import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import { getUserData } from "../Helpers/utils";

const PROGRAM = "DXP";
const POLL_MS = 200;
const MAX_ATTEMPTS = 150;

/**
 * Mirrors pre-migration PartnerDetailsForm: after Adobe ID sign-in, compare the
 * email searched on step 1 with the signed-in Adobe ID email and route to the
 * mismatch page when they differ.
 */
export function useDxpEmailMismatchGuard(active: boolean): boolean {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(active);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      resolvedRef.current = false;
      setIsChecking(false);
      return () => undefined;
    }

    setIsChecking(true);

    if (resolvedRef.current) {
      setIsChecking(false);
      return () => undefined;
    }

    if (localStorage.getItem("emailMismatch")) {
      resolvedRef.current = true;
      setIsChecking(false);
      return () => undefined;
    }

    if (!window.adobeIMS?.isSignedInUser?.()) {
      resolvedRef.current = true;
      setIsChecking(false);
      return () => undefined;
    }

    let attempts = 0;
    let cancelled = false;

    const finish = () => {
      if (cancelled || resolvedRef.current) return;
      resolvedRef.current = true;
      setIsChecking(false);
    };

    const tryCheck = () => {
      if (cancelled || resolvedRef.current) return;

      if (localStorage.getItem("emailMismatch")) {
        finish();
        return;
      }

      const searchedEmail = (localStorage.getItem("email") || "").trim();
      const imsEmail = getUserData()?.email?.trim() || "";

      if (!searchedEmail) {
        finish();
        return;
      }

      if (!imsEmail) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          finish();
        }
        return;
      }

      if (searchedEmail.toLowerCase() !== imsEmail.toLowerCase()) {
        navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.emailMismatch);
      }

      finish();
    };

    tryCheck();
    const poll = window.setInterval(tryCheck, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [active, navigate]);

  return isChecking;
}

export default useDxpEmailMismatchGuard;
